import { ChatMessage } from '../types';
import llmCredentialsService from './llmCredentialsService';

export interface ActivityMethod {
  activityType: string;
  instructions: string;
  items?: string[];
  expectedAnswers?: string[];
  correctIndexes?: number[];
  story?: string;
  prompt?: string;
}

export interface ActivityAnalysisResult {
  objetivo: string;
  nivel: string;
  metodoActividad: ActivityMethod;
  vocabulario: string;
  nombreActividad: string;
  grupo: string;
}

const normalizeLevel = (nivel: string): string => {
  const match = nivel.match(/\b(A1|A2|B1|B2|C1|C2)\b/i);
  return match ? match[1].toUpperCase() : nivel.trim();
};

const sanitizeJsonResponse = (content: string): string => {
  const trimmed = content.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    return trimmed;
  }
  const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    return jsonMatch[0];
  }
  return trimmed;
};

const buildConversationText = (messages: ChatMessage[]): string => {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => `${m.role.toUpperCase()}: ${m.content}`)
    .join('\n');
};

export const activityAnalysisService = {
  async analyzeConversation(messages: ChatMessage[]): Promise<ActivityAnalysisResult> {
    const conversationText = buildConversationText(messages);
    if (!conversationText.trim()) {
      throw new Error('No hay conversación disponible para analizar.');
    }

    let credentials = llmCredentialsService.getCredentials();
    if (!credentials?.openaiKey) {
      credentials = await llmCredentialsService.loginAndGetCredentials();
    }

    if (!credentials?.openaiKey) {
      throw new Error('No se pudo obtener la API key de OpenAI');
    }

    const systemPrompt = `You will analyze the COMPLETE conversation provided from a LearnEnglish teacher session.
Return ONLY a valid JSON object in this EXACT format:
{
  "objetivo": "<main learning objective>",
  "nivel": "<CEFR level: A1, A2, B1, B2, C1, or C2>",
  "metodoActividad": {
    "activityType": "<fill_blanks|story_continue|choose_correct|sentence_correction|conversation|test|other>",
    "instructions": "<clear instruction for the student>",
    "items": ["<sentence 1>", "<sentence 2>"],
    "expectedAnswers": ["<answer 1>", "<answer 2>"],
    "correctIndexes": [0, 2],
    "story": "<story text if applicable>",
    "prompt": "<continuation prompt if applicable>"
  },
  "vocabulario": "<key vocabulary, comma-separated if possible>",
  "nombreActividad": "<short activity name>",
  "grupo": "<group name or General if missing>"
}
Rules:
- Use ONLY the info from the conversation. Do NOT invent.
- If a field is missing, return an empty string, except grupo which must be "General" if missing.
- Use 0-based indexes in correctIndexes.
- For fill_blanks: include items + expectedAnswers.
- For choose_correct: include items + correctIndexes.
- For story_continue: include story + prompt.
- Keep JSON valid with double quotes.
- Respond ONLY with JSON and no extra text.`;

    const userPrompt = `Conversation:\n${conversationText}`;

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${credentials.openaiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        temperature: 0.2,
        response_format: { type: 'json_object' },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content;
    if (!content) {
      throw new Error('No se recibió contenido de OpenAI');
    }

    let parsed: ActivityAnalysisResult;
    try {
      parsed = JSON.parse(sanitizeJsonResponse(content));
    } catch (error) {
      throw new Error(`No se pudo parsear JSON de respuesta: ${content}`);
    }

    if (!parsed.metodoActividad || typeof parsed.metodoActividad !== 'object') {
      throw new Error('Respuesta de GPT inválida: falta metodoActividad estructurado');
    }

    return {
      ...parsed,
      nivel: normalizeLevel(parsed.nivel || ''),
      grupo: parsed.grupo?.trim() || 'General',
    };
  },
};

export default activityAnalysisService;
